import { Injectable, Inject, RendererFactory2, Renderer2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { TinyColor } from '@ctrl/tinycolor';
import { ThemeColors, ThemeColorName, ThemeMode, Theme, ThemeSurface } from 'shared';
import { ColorModeService } from '@coreui/angular-pro';

@Injectable({ providedIn: 'root' })
export class ThemeService {

  private renderer: Renderer2;
  private currentMode: ThemeMode = 'light';

  /** Angular Material MD2-style shade curve */
  private shadeCurve = {
    50:  52,
    100: 37,
    200: 26,
    300: 12,
    400:  6,
    500:  0,
    600: -6,
    700: -12,
    800: -18,
    900: -24
  } as const;

  /** MD2 Accent Curve for A100–A700 */
  private accentCurve = {
    A100: 50,
    A200: 30,
    A400: 10,
    A700: -10
  } as const;

  constructor(
    rendererFactory: RendererFactory2,
    private colorModeService: ColorModeService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  // ======================================================================
  // PUBLIC API
  // ======================================================================
  applyTheme(theme: Partial<Theme> = {}) {
    const savedMode = localStorage.getItem('theme-mode') as ThemeMode | null;
    const preferredMode = this.detectPreferredMode();
    const mode: ThemeMode = theme.mode || savedMode || preferredMode || 'light';

    this.currentMode = mode;
    this.colorModeService.colorMode.set(mode);

    const defaultColors: ThemeColors = {
      primary: '#0d6efd',
      secondary: '#6c757d',
      success: '#198754',
      info: '#0dcaf0',
      warning: '#ffc107',
      danger: '#dc3545',
      light: '#f8f9fa',
      dark: '#212529'
    };
    const colors: ThemeColors = { ...defaultColors, ...(theme.colors || {}) };

    // --- CoreUI palette ---
    this.generateCoreUIPalette(colors, mode);
    this.applySurface(theme.surface);

    // --- Primary colors ---
    this.generatePrimaryColorPalette(colors.primary, mode);
    this.generatePrimaryContrastPalette(colors.primary, mode);

    // --- Accent colors ---
    this.generateAccentColorPalette(colors.primary, mode);
    this.generateAccentContrastPalette(colors.primary, mode);

    localStorage.setItem('theme-mode', mode);
  }

  toggleMode() {
    this.applyTheme({ mode: this.currentMode === 'light' ? 'dark' : 'light' });
  }

  // ======================================================================
  // COREUI PALETTE GENERATION
  // ======================================================================
  private generateCoreUIPalette(colors: ThemeColors, mode: ThemeMode) {
    this.applyBaseSemanticColors(mode);
    this.applyGrayScale(mode);

    (Object.keys(colors) as ThemeColorName[]).forEach((key) => {
      const value = colors[key];
      const base = new TinyColor(value);
      if (!base.isValid) return;

      const rgb = base.toRgb();
      this.set(`--cui-${key}`, base.toHexString());
      this.set(`--cui-${key}-rgb`, `${rgb.r}, ${rgb.g}, ${rgb.b}`);

      this.set(`--cui-${key}-text-emphasis`, this.generateTextEmphasis(base, mode));
      this.set(`--cui-${key}-bg-subtle`, this.generateSubtleBg(base, mode));
      this.set(`--cui-${key}-border-subtle`, this.generateSubtleBorder(base, mode));

      const grad = this.generateGradientStops(base, mode);
      this.set(`--cui-${key}-start`, grad.start);
      this.set(`--cui-${key}-stop`, grad.stop);
    });

    this.applyLinkColors(colors.primary);

    this.set('--cui-code-color', '#e684b4');
    this.set('--cui-highlight-color', 'rgba(255,255,255,.87)');
    this.set('--cui-highlight-bg', 'rgb(102,77.2,2.8)');
  }

  /** App-pinned surface colours (Theme.surface) on top of the mode defaults. */
  private applySurface(surface?: ThemeSurface) {
    if (!surface) return;
    const map: Record<string, string | undefined> = {
      '--cui-body-bg': surface.bodyBg,
      '--cui-body-color': surface.bodyColor,
      '--cui-secondary-bg': surface.secondaryBg,
      '--cui-secondary-color': surface.secondaryColor,
      '--cui-tertiary-bg': surface.tertiaryBg,
      '--cui-tertiary-color': surface.tertiaryColor,
      '--cui-border-color': surface.borderColor
    };
    Object.entries(map).forEach(([name, value]) => {
      if (!value) return;
      this.set(name, value);
      this.copyRgb(name);
    });
  }

  private applyBaseSemanticColors(mode: ThemeMode) {
    if (mode === 'dark') {
      this.set('--cui-body-bg', '#212631');
      this.set('--cui-body-color', 'rgba(255,255,255,.87)');
      this.set('--cui-secondary-bg', '#323a49');
      this.set('--cui-secondary-color', 'rgba(255,255,255,.6)');
      this.set('--cui-tertiary-bg', 'rgb(41.5,48,61)');
      this.set('--cui-tertiary-color', 'rgba(255,255,255,.38)');
    } else {
      this.set('--cui-body-bg', '#ffffff');
      this.set('--cui-body-color', '#212529');
      this.set('--cui-secondary-bg', '#f0f2f5');
      this.set('--cui-secondary-color', '#6c757d');
      this.set('--cui-tertiary-bg', '#e9ecef');
      this.set('--cui-tertiary-color', '#495057');
    }

    this.copyRgb('--cui-body-bg');
    this.copyRgb('--cui-body-color');
    this.copyRgb('--cui-secondary-bg');
    this.copyRgb('--cui-secondary-color');
    this.copyRgb('--cui-tertiary-bg');
    this.copyRgb('--cui-tertiary-color');
  }

  private applyGrayScale(mode: ThemeMode) {
    const dark = ['#f3f4f7','#e7eaee','#dbdfe6','#cfd4de','#aab3c5','#6d7d9c','#4a566d','#323a49','#212631'];
    const light = ['#f8f9fa','#e9ecef','#dee2e6','#ced4da','#adb5bd','#6c757d','#495057','#343a40','#212529'];
    const list = mode === 'dark' ? dark : light;
    list.forEach((val, idx) => this.set(`--cui-gray-${(idx + 1) * 100}`, val));
  }

  private generateTextEmphasis(col: TinyColor, mode: ThemeMode) {
    return (mode === 'dark' ? col.clone().darken(35) : col.clone().darken(35).desaturate(15)).toRgbString();
  }

  private generateSubtleBg(col: TinyColor, mode: ThemeMode) {
    return (mode === 'dark' ? col.clone().lighten(15) : col.clone().lighten(60)).toHexString();
  }

  private generateSubtleBorder(col: TinyColor, mode: ThemeMode) {
    return (mode === 'dark' ? col.clone().lighten(20) : col.clone().darken(20)).toHexString();
  }

  private generateGradientStops(col: TinyColor, mode: ThemeMode) {
    return {
      start: mode === 'dark' ? col.clone().lighten(25).toRgbString() : col.clone().lighten(10).toRgbString(),
      stop:  mode === 'dark' ? col.clone().lighten(15).toRgbString() : col.clone().lighten(5).toRgbString()
    };
  }

  private applyLinkColors(primary: string) {
    const base = new TinyColor(primary);
    const hover = base.clone().lighten(8);
    this.set('--cui-link-color', base.toRgbString());
    this.set('--cui-link-hover-color', hover.toRgbString());
    const r = base.toRgb();
    const hr = hover.toRgb();
    this.set('--cui-link-color-rgb', `${r.r},${r.g},${r.b}`);
    this.set('--cui-link-hover-color-rgb', `${hr.r},${hr.g},${hr.b}`);
  }

  private copyRgb(varName: string) {
    const val = getComputedStyle(this.document.documentElement).getPropertyValue(varName).trim();
    const rgb = new TinyColor(val).toRgb();
    this.set(`${varName}-rgb`, `${rgb.r}, ${rgb.g}, ${rgb.b}`);
  }

  // ======================================================================
  // MATERIAL-STYLE PRIMARY + ACCENT PALETTES
  // ======================================================================
  private generatePrimaryColorPalette(primary: string, mode: ThemeMode) {
    const base = new TinyColor(primary);
    (Object.keys(this.shadeCurve) as unknown as Array<keyof typeof this.shadeCurve>).forEach((tone) => {
      const shift = this.shadeCurve[tone];
      const color = this.getShadeForMode(base, shift, mode);
      const hex = color.toHexString();
      const rgb = color.toRgb();
      this.set(`--color-primary-${tone}`, hex);
      this.set(`--color-primary-${tone}-rgb`, `${rgb.r}, ${rgb.g}, ${rgb.b}`);
    });
    this.set('--color-primary', base.toHexString());
  }

  private generatePrimaryContrastPalette(primary: string, mode: ThemeMode) {
    const base = new TinyColor(primary);
    (Object.keys(this.shadeCurve) as unknown as Array<keyof typeof this.shadeCurve>).forEach((tone) => {
      const shift = this.shadeCurve[tone];
      const color = this.getShadeForMode(base, shift, mode);
      this.set(`--color-primaryContrast-${tone}`, this.materialContrast(color));
    });
    this.set('--color-primaryContrast', this.materialContrast(base));
  }

  private generateAccentColorPalette(primary: string, mode: ThemeMode) {
    const base = new TinyColor(primary);
    (Object.keys(this.accentCurve) as unknown as Array<keyof typeof this.accentCurve>).forEach((tone) => {
      const shift = this.accentCurve[tone];
      const color = this.getShadeForMode(base, shift, mode);
      const hex = color.toHexString();
      const rgb = color.toRgb();
      this.set(`--color-primary-${tone}`, hex);
      this.set(`--color-primary-${tone}-rgb`, `${rgb.r}, ${rgb.g}, ${rgb.b}`);
    });
  }

  private generateAccentContrastPalette(primary: string, mode: ThemeMode) {
    const base = new TinyColor(primary);
    (Object.keys(this.accentCurve) as unknown as Array<keyof typeof this.accentCurve>).forEach((tone) => {
      const shift = this.accentCurve[tone];
      const color = this.getShadeForMode(base, shift, mode);
      this.set(`--color-primaryContrast-${tone}`, this.materialContrast(color));
    });
  }

  private getShadeForMode(base: TinyColor, shift: number, mode: ThemeMode): TinyColor {
    if (mode === 'dark') shift = -shift;
    if (shift > 0) return base.clone().lighten(shift);
    if (shift < 0) return base.clone().darken(Math.abs(shift));
    return base.clone();
  }

  private materialContrast(tc: TinyColor): string {
    return tc.getLuminance() > 0.5 ? 'rgba(0,0,0,0.87)' : '#ffffff';
  }

  private set(name: string, value: string) {
    this.document.documentElement.style.setProperty(name, value);
  }

  private detectPreferredMode(): ThemeMode {
    return matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
}
